/**
 * Shared types for the RUM (Real User Monitoring) first-party telemetry drain.
 *
 * Kept dependency-free (no `~/env`, no DB, no Next imports) — mirrors
 * `~/lib/gsc/types` — so both the Next.js runtime (the ingest route) and the
 * standalone `tsx` analyze script can import them without dragging in the
 * t3-env bundler chain.
 */

/** Core Web Vitals + the two diagnostics we also store. */
export type RumMetricName = "LCP" | "INP" | "CLS" | "FCP" | "TTFB"

/** The Core Web Vitals trio that ladders into ledger opportunities. The
 *  diagnostics (FCP/TTFB) are stored for context but never open a finding. */
export const CORE_WEB_VITALS: readonly RumMetricName[] = ["LCP", "INP", "CLS"]

/** Coarse device class derived from viewport width at sample time. */
export type RumDevice = "mobile" | "desktop"

/** One Web Vitals sample as it arrives on the ingest beacon (pre-storage). */
export interface WebVitalSample {
  path: string
  metric: RumMetricName
  value: number
  rating?: string
  device?: RumDevice
  navigationType?: string
}

/** One first-party event sample as it arrives on the ingest beacon. */
export interface RumEventSample {
  name: string
  path: string
  props?: Record<string, unknown>
}

/**
 * The ONLY event-property keys the drain forwards/stores — small, non-PII,
 * rule-relevant. Enforced on BOTH the client (what the beacon sends) and the
 * server (the ingest route strips everything else before persisting), so a
 * direct caller can't smuggle `email`/`session_id`/other PII into `rum_events`.
 */
export const FORWARDED_EVENT_PROP_KEYS = [
  "resource_slug",
  "resource_type",
  "resource_section",
  "outcome",
  "interaction_type",
  "element_id",
  // Exception diagnostics. Every `exception` row stored `props = {}` because
  // none of these were forwarded — a client-error spike was countable but not
  // diagnosable. These are the SAME fields `trackException` already sends to
  // GA4; withholding them from first-party storage collected strictly less
  // than GA4 already had, for no legal reason
  // (docs/analytics/first-party-analytics-posture.md).
  //
  // `description` is a first-party error message from our own boundaries. It
  // is bounded for transport, NOT redacted: no law requires degrading it, and
  // the posture forbids reducing first-party collection absent a named legal
  // requirement.
  "description",
  "component",
  "error_id",
  "fatal",
] as const

/**
 * TRANSPORT-ONLY dedupe token for `resource_view` (r3547249797). The drain
 * stamps a fresh per-enqueue token on every `resource_view` it buffers —
 * `ResourceAnalytics` single-fires per mount, so one enqueue = one mount —
 * and the ingest route dedupes views on (path, token) instead of path alone:
 * identical stacked copies still collapse, but distinct legitimate mounts
 * (SPA navigation back to the same resource before a flush) all count toward
 * the funnel denominator. NOT in `FORWARDED_EVENT_PROP_KEYS` semantics of
 * "stored": the route strips it after deduping, so it never reaches
 * `rum_events.props`.
 */
export const RUM_VIEW_INSTANCE_KEY = "view_instance"

/** Per-(request, path) ceiling on distinct-token `resource_view`s — bounds the
 *  spoofed-token stacking vector while comfortably covering legitimate SPA
 *  back-and-forth inside one flush window (see the ingest route's dedupe). */
export const MAX_VIEWS_PER_PATH_PER_REQUEST = 5

/**
 * The `resource_view` `resource_type` values whose pages have a completion
 * action — i.e. can ever emit `resource_complete`. The funnel aggregation
 * counts VIEWS only for these types, so content-only pages (guides, glossary,
 * ebooks, templates, analyzers) that mount `ResourceAnalytics` but have no
 * completion action can't surface as 0%-conversion `low_resource_conversion`
 * findings (`r3540663151`, the follow-up promised on `r3463922226`).
 *
 * Derived from the actual `sendResourceEvent('complete', …)` call sites:
 *   - calculators emit `resource_type: 'calculator'`
 *   - the quiz flow emits `'quiz'`
 *   - generator export/copy paths (shared `exportImage`, UtmBuilder,
 *     ColorPalette, ChartMockup, DataTable, BuyerPersona PDF) emit `'tool'`,
 *     while their PAGES mount `ResourceAnalytics` as `'generator'` — so both
 *     types are completable ('tool' also covers any future page that views
 *     under the same type its completes use).
 *
 * MAINTENANCE: adding a completion action to a NEW resource type requires
 * adding that type here, or its pages' genuine low-conversion signal is
 * silently excluded from the funnel (grep for `sendResourceEvent('complete'`).
 */
export const COMPLETABLE_RESOURCE_TYPES = [
  "calculator",
  "generator",
  "quiz",
  "tool",
] as const

/**
 * Per-(path, metric, device) Web Vitals aggregate over an analysis window — the
 * analyzer input for the `poor_web_vitals` rule. `p75` is the 75th-percentile
 * value (the percentile Google's Core Web Vitals assessment uses). Segmenting by
 * device prevents a healthy majority device (e.g. desktop) from masking a
 * failing minority one (e.g. mobile).
 */
export interface WebVitalsAggregate {
  path: string
  metric: RumMetricName
  /** 'mobile' | 'desktop' | 'unknown' (coalesced from the sample's device). */
  device: string
  samples: number
  p75: number
}

/**
 * Per-page resource funnel aggregate over a window — the analyzer input for the
 * `low_resource_conversion` rule. `views` counts `resource_view` events and
 * `completes` counts `resource_complete` events on that path.
 */
export interface ResourceFunnelAggregate {
  path: string
  views: number
  completes: number
}

/** The opportunity kinds the RUM analyzer can emit. Mirrors the kinds added to
 *  the `seo_opportunities.kind` CHECK constraint in migration 0011. */
export type RumOpportunityKind = "poor_web_vitals" | "low_resource_conversion"

/**
 * A single programmatic RUM opportunity produced by the analyzer.
 *
 * Structurally compatible with `~/lib/gsc/types`'s `SeoOpportunity` so it writes
 * into the same `seo_opportunities` ledger. The SEO-specific numeric columns are
 * repurposed honestly where they map and left 0 otherwise (documented per rule):
 * for `low_resource_conversion`, `impressions`=views, `clicks`=completes,
 * `ctr`=completion rate; for `poor_web_vitals`, `impressions`=sample count and
 * the metric/p75/threshold detail lives in `evidence`.
 */
export interface RumOpportunity {
  kind: RumOpportunityKind
  /** Pathname the opportunity attaches to. */
  page: string
  /** Ledger key discriminator: the metric name for `poor_web_vitals` (so a page
   *  missing several Core Web Vitals yields distinct rows), else "". */
  query: string
  /** Priority score (higher = act sooner). */
  score: number
  impressions: number
  clicks: number
  ctr: number
  position: number
  /** Human-readable one-line recommendation. */
  summary: string
  /** Structured evidence: the rule inputs + computed values. */
  evidence: Record<string, unknown>
}
